/**
 * What's new: Crew's updates for the people who use it, one Markdown file
 * each in `updates/` (docs/agents/changelog.md). `pnpm check` holds each to
 * a short title, a category and 150 words (scripts/changelog.mjs).
 */
import { parseFrontmatter } from "./frontmatter.mjs";

export type UpdateCategory = "feature" | "improvement" | "fix";

export type Update = {
  id: string;
  title: string;
  /** `YYYY-MM-DD`, the day it shipped. */
  date: string;
  category: UpdateCategory;
  body: string;
};

export const CATEGORY_LABELS: Record<UpdateCategory, string> = {
  feature: "Feature",
  improvement: "Improvement",
  fix: "Fix",
};

const CATEGORIES = Object.keys(CATEGORY_LABELS) as UpdateCategory[];

/** An update's file, by its path and text; null when it isn't one. */
export function parseUpdate(path: string, text: string): Update | null {
  const parsed = parseFrontmatter(text);
  if (!parsed) return null;
  const { data, body } = parsed;
  const category = data.category as UpdateCategory;
  if (!data.title || !/^\d{4}-\d{2}-\d{2}$/.test(data.date ?? "")) return null;
  if (!CATEGORIES.includes(category)) return null;
  const id = path.replace(/^.*\//, "").replace(/\.md$/, "");
  return { id, title: data.title, date: data.date, category, body: body.trim() };
}

/** Newest first; on the same day, by file name, so the order never shifts. */
export function sortUpdates(updates: Update[]): Update[] {
  return [...updates].sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id));
}

/** The updates, grouped by month, newest first: "October 2026". */
export function byMonth(updates: Update[]): { month: string; updates: Update[] }[] {
  const groups: { month: string; updates: Update[] }[] = [];
  for (const update of updates) {
    const month = monthLabel(update.date);
    const last = groups.at(-1);
    if (last?.month === month) last.updates.push(update);
    else groups.push({ month, updates: [update] });
  }
  return groups;
}

const at = (date: string) => new Date(`${date}T12:00:00Z`);

const monthLabel = (date: string) =>
  at(date).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

/** "Oct 15". */
export const dayLabel = (date: string) =>
  at(date).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const files = import.meta.glob<string>("./updates/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

export const UPDATES: Update[] = sortUpdates(
  Object.entries(files).flatMap(([path, text]) => parseUpdate(path, text) ?? []),
);

/*
 * Which updates this person has seen, in this browser: the ids, kept when
 * What's new opens. Its row's copper dot (and the phone's More tab's) shows while any isn't.
 */
type Store = Pick<Storage, "getItem" | "setItem">;

const seenKey = (userId: string) => `crew:whats-new:seen:${userId}`;

/** The browser's storage, or none where it is blocked (a private window can throw). */
function browserStore(): Store | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();

/**
 * For `useSyncExternalStore`: told when What's new marks updates seen, in
 * this tab or, through the `storage` event, in another.
 */
export function subscribeSeen(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith("crew:whats-new:seen:")) listener();
  };
  globalThis.addEventListener?.("storage", onStorage);
  return () => {
    listeners.delete(listener);
    globalThis.removeEventListener?.("storage", onStorage);
  };
}

/** Whether any of `updates` hasn't been seen by this person in this browser. */
export function hasUnseen(userId: string, updates = UPDATES, store = browserStore()): boolean {
  if (updates.length === 0) return false;
  let seen: unknown;
  try {
    seen = JSON.parse(store?.getItem(seenKey(userId)) ?? "[]");
  } catch {
    seen = [];
  }
  const ids = new Set(Array.isArray(seen) ? seen : []);
  return updates.some((update) => !ids.has(update.id));
}

/** Every one of `updates` seen: the dot goes. */
export function markSeen(userId: string, updates = UPDATES, store = browserStore()) {
  try {
    store?.setItem(seenKey(userId), JSON.stringify(updates.map((update) => update.id)));
  } catch {
    // A full or blocked store just means the dot stays.
  }
  for (const listener of listeners) listener();
}
